import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { landingPageAPI, branchAPI } from '../../services/api';
import { useAuthStore } from '../../store';
import usePermission from '../../hooks/usePermission';
import './LandingPages.css';

function fmt(date) {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function LandingPages() {
    const navigate = useNavigate();
    const admin = useAuthStore((s) => s.admin);
    const { canCreate, canDelete, canConfigure } = usePermission('landing_pages');

    const [pages, setPages] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [branchFilter, setBranchFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [createForm, setCreateForm] = useState({ title: '', slug: '', branchId: '', blank: false });
    const [creating, setCreating] = useState(false);

    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [pageToDelete, setPageToDelete] = useState(null);

    // Branches dialog — the page it is open for, the branch picked, and a request in flight.
    const [branchPage, setBranchPage] = useState(null);
    const [branchChoice, setBranchChoice] = useState('');
    const [branchBusy, setBranchBusy] = useState(false);

    const fetchPages = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (branchFilter) params.branchId = branchFilter;
            if (statusFilter) params.status = statusFilter;
            const res = await landingPageAPI.getAll(params);
            const list = res.data.data || [];
            setPages(list);
            return list;
        } catch (error) {
            toast.error('Failed to fetch landing pages');
            return null;
        } finally {
            setLoading(false);
        }
    }, [branchFilter, statusFilter]);

    useEffect(() => { fetchPages(); }, [fetchPages]);

    useEffect(() => {
        branchAPI.getAll(admin?.role === 'superadmin' ? undefined : admin?.tenantId)
            .then((res) => setBranches(res.data.data?.branches || res.data.data || []))
            .catch(() => {});
    }, [admin]);

    function slugify(text) {
        return text.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)+/g, '');
    }

    async function handleCreate(e) {
        e.preventDefault();
        if (!createForm.title || !createForm.slug) {
            toast.warning('Title and slug are required');
            return;
        }

        // A superadmin has no tenant of their own — the backend requires an
        // explicit tenantId for them and can't infer one, so the branch
        // picker doubles as the tenant picker: derive it from the selected
        // branch's already-populated tenantId (branchAPI.getAll populates
        // it with name/slug/type for exactly this reason).
        let tenantId;
        if (admin?.role === 'superadmin') {
            if (!createForm.branchId) {
                toast.warning('Select a branch — as superadmin, this is how the page\'s tenant is determined');
                return;
            }
            const branch = branches.find((b) => b._id === createForm.branchId);
            tenantId = branch?.tenantId?._id || branch?.tenantId;
            if (!tenantId) {
                toast.error('Could not determine the tenant for the selected branch');
                return;
            }
        }

        setCreating(true);
        try {
            const res = await landingPageAPI.create({
                title: createForm.title,
                slug: createForm.slug,
                branchId: createForm.branchId || undefined,
                tenantId,
                // Left out, the server starts the page with the default design.
                ...(createForm.blank ? { nodes: [] } : {}),
            });
            toast.success('Landing page created');
            setShowCreateModal(false);
            setCreateForm({ title: '', slug: '', branchId: '', blank: false });
            navigate(`/landing-pages/${res.data.data._id}/edit`);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to create landing page');
        } finally {
            setCreating(false);
        }
    }

    async function handleDuplicate(id) {
        try {
            await landingPageAPI.duplicate(id);
            toast.success('Landing page duplicated');
            fetchPages();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to duplicate');
        }
    }

    async function handlePublishToggle(page) {
        try {
            if (page.status === 'published') {
                await landingPageAPI.unpublish(page._id);
                toast.success('Unpublished');
            } else {
                await landingPageAPI.publish(page._id);
                toast.success('Published');
            }
            fetchPages();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update publish status');
        }
    }

    function handleDelete(page) {
        setPageToDelete(page);
        setShowDeleteModal(true);
    }

    async function confirmDelete() {
        if (!pageToDelete) return;
        try {
            await landingPageAPI.delete(pageToDelete._id);
            toast.success('Landing page deleted');
            fetchPages();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete landing page');
        } finally {
            setShowDeleteModal(false);
            setPageToDelete(null);
        }
    }

    // The branches a page can still go on: its own branch, or — for a tenant-wide page —
    // every branch of its company; minus the ones already showing it.
    function branchesFor(page) {
        const ownBranchId = page.branchId?._id || page.branchId;
        const live = new Set((page.liveOn || []).map((b) => String(b._id)));
        return branches.filter((b) => {
            if (live.has(String(b._id))) return false;
            if (ownBranchId) return String(b._id) === String(ownBranchId);
            return String(b.tenantId?._id || b.tenantId) === String(page.tenantId);
        });
    }

    function openBranches(page) {
        const options = branchesFor(page);
        setBranchPage(page);
        setBranchChoice(options.length === 1 ? options[0]._id : '');
    }

    // After a change the dialog shows the page as the server now has it.
    async function reloadBranchPage(pageId) {
        const list = await fetchPages();
        const fresh = list?.find((p) => p._id === pageId);
        setBranchPage(fresh || null);
        setBranchChoice('');
    }

    async function handleUseOnBranch() {
        if (!branchPage || !branchChoice) return;
        setBranchBusy(true);
        try {
            const res = await landingPageAPI.setDefault(branchPage._id, branchChoice);
            toast.success(res.data?.message || 'Now live on the branch');
            await reloadBranchPage(branchPage._id);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to use the page on the branch');
        } finally {
            setBranchBusy(false);
        }
    }

    async function handleStopOnBranch(branch) {
        if (!branchPage) return;
        setBranchBusy(true);
        try {
            const res = await landingPageAPI.unsetDefault(branchPage._id, branch._id);
            toast.success(res.data?.message || 'The branch is back on its classic portal page');
            await reloadBranchPage(branchPage._id);
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to stop using the page on the branch');
        } finally {
            setBranchBusy(false);
        }
    }

    async function handleSaveAsTemplate(page) {
        try {
            await landingPageAPI.saveAsTemplate(page._id, {});
            toast.success('Saved to the shared template library');
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to save as template');
        }
    }

    return (
        <div className="page-container-modern">
            <div className="page-header-modern">
                <div className="header-content">
                    <div className="header-text">
                        <h1 className="page-title-modern">
                            <i className="fas fa-paint-brush"></i>
                            Landing Pages
                        </h1>
                        <p className="page-subtitle-modern">Build and manage visual captive-portal landing pages</p>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button className="btn-secondary-modern" onClick={() => navigate('/landing-pages/templates')}>
                            <i className="fas fa-th-large"></i> Templates
                        </button>
                        {canCreate && (
                            <button className="btn-primary-modern" onClick={() => setShowCreateModal(true)}>
                                <i className="fas fa-plus"></i> New Landing Page
                            </button>
                        )}
                    </div>
                </div>
            </div>

            <div className="filters-section">
                <div className="filter-group">
                    <select className="filter-select" value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}>
                        <option value="">All Branches</option>
                        {branches.map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                    </select>
                </div>
                <div className="filter-group">
                    <select className="filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="">All Status</option>
                        <option value="draft">Draft</option>
                        <option value="published">Published</option>
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="loading-modern">
                    <div className="spinner-modern"></div>
                    <p>Loading landing pages...</p>
                </div>
            ) : pages.length === 0 ? (
                <div className="empty-state-modern">
                    <div className="empty-icon"><i className="fas fa-paint-brush"></i></div>
                    <h3>No Landing Pages Yet</h3>
                    <p>Create your first visual landing page to get started.</p>
                </div>
            ) : (
                <div className="resource-grid-modern">
                    {pages.map((page) => (
                        <div key={page._id} className="resource-card-modern">
                            <div className="card-header">
                                <div className="resource-title">
                                    <h3 title={page.title}>{page.title}</h3>
                                    <span className="resource-subtitle">/{page.slug} • {page.branchId?.name || 'Tenant-wide'}</span>
                                </div>
                                <span className={`status-badge ${page.status === 'published' ? 'active' : 'inactive'}`}>
                                    {page.status}
                                </span>
                            </div>
                            <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: '8px 0' }}>
                                Updated {fmt(page.updatedAt)} by {page.updatedBy?.name || '—'}
                            </p>
                            {page.liveOn?.length > 0 && (
                                <p className="lp-live-on" title="Guests of these branches see this page">
                                    <i className="fas fa-broadcast-tower"></i> Live on {page.liveOn.map((b) => b.name).join(', ')}
                                </p>
                            )}
                            <div className="card-actions" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                <button className="action-btn-icon" title="Edit" onClick={() => navigate(`/landing-pages/${page._id}/edit`)}>
                                    <i className="fas fa-edit"></i>
                                </button>
                                {canCreate && (
                                    <button className="action-btn-icon" title="Duplicate" onClick={() => handleDuplicate(page._id)}>
                                        <i className="fas fa-copy"></i>
                                    </button>
                                )}
                                {canConfigure && (
                                    <button
                                        className="action-btn-icon"
                                        title={page.status === 'published' ? 'Unpublish' : 'Publish'}
                                        onClick={() => handlePublishToggle(page)}
                                    >
                                        <i className={`fas ${page.status === 'published' ? 'fa-eye-slash' : 'fa-globe'}`}></i>
                                    </button>
                                )}
                                {canConfigure && (page.status === 'published' || page.liveOn?.length > 0) && (
                                    <button className="action-btn-icon" title="Branches — use this page on a branch, or stop" onClick={() => openBranches(page)}>
                                        <i className="fas fa-code-branch"></i>
                                    </button>
                                )}
                                {admin?.role === 'superadmin' && (
                                    <button className="action-btn-icon" title="Save as shared template" onClick={() => handleSaveAsTemplate(page)}>
                                        <i className="fas fa-th-large"></i>
                                    </button>
                                )}
                                {canDelete && (
                                    <button className="action-btn-icon delete" title="Delete" onClick={() => handleDelete(page)}>
                                        <i className="fas fa-trash"></i>
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showCreateModal && (
                <div className="modal-overlay-modern" onClick={() => setShowCreateModal(false)}>
                    <div className="modal-modern" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div><h2>New Landing Page</h2></div>
                            <button className="modal-close" onClick={() => setShowCreateModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <form onSubmit={handleCreate}>
                            <div className="modal-body-modern">
                                <div className="form-group">
                                    <label>Title</label>
                                    <input
                                        className="form-control"
                                        value={createForm.title}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, title: e.target.value, slug: f.slug || slugify(e.target.value) }))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        className="form-control"
                                        value={createForm.slug}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, slug: slugify(e.target.value) }))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Branch{admin?.role === 'superadmin' ? ' (required)' : ''}</label>
                                    <select
                                        className="form-control"
                                        value={createForm.branchId}
                                        onChange={(e) => setCreateForm((f) => ({ ...f, branchId: e.target.value }))}
                                        required={admin?.role === 'superadmin'}
                                    >
                                        <option value="">
                                            {admin?.role === 'superadmin' ? 'Select a branch...' : 'Tenant-wide (no specific branch)'}
                                        </option>
                                        {branches.map((b) => (
                                            <option key={b._id} value={b._id}>
                                                {b.name}{admin?.role === 'superadmin' && b.tenantId?.name ? ` — ${b.tenantId.name}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                {/* What the page starts with: the standard design, or nothing at all
                                    for a design of your own. Other ready-made designs are in Templates. */}
                                <fieldset className="form-group lp-start-choice">
                                    <legend>Design</legend>
                                    <label className={`lp-start-option${!createForm.blank ? ' selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="startWith"
                                            checked={!createForm.blank}
                                            onChange={() => setCreateForm((f) => ({ ...f, blank: false }))}
                                        />
                                        <span className="lp-start-text">
                                            <strong>Default design</strong>
                                            <small>Starts with the standard design — the company logo on a brand-colour panel beside a clean sign-in column, in the logo and colour from Branding. Change anything, or delete what you do not need.</small>
                                        </span>
                                    </label>
                                    <label className={`lp-start-option${createForm.blank ? ' selected' : ''}`}>
                                        <input
                                            type="radio"
                                            name="startWith"
                                            checked={createForm.blank}
                                            onChange={() => setCreateForm((f) => ({ ...f, blank: true }))}
                                        />
                                        <span className="lp-start-text">
                                            <strong>Custom design</strong>
                                            <small>The builder opens empty — build your own design from scratch. Duplicate it later to reuse it for another page.</small>
                                        </span>
                                    </label>
                                    <button
                                        type="button"
                                        className="lp-start-templates"
                                        onClick={() => { setShowCreateModal(false); navigate('/landing-pages/templates'); }}
                                    >
                                        <i className="fas fa-th-large"></i> Or start from another ready-made design in Templates
                                    </button>
                                </fieldset>
                            </div>
                            <div className="modal-footer-modern">
                                <button type="button" className="btn-secondary-modern" onClick={() => setShowCreateModal(false)}>Cancel</button>
                                <button type="submit" className="btn-primary-modern" disabled={creating}>
                                    {creating ? 'Creating...' : 'Create & Open Builder'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {branchPage && (
                <div className="modal-overlay-modern" onClick={() => setBranchPage(null)}>
                    <div className="modal-modern" role="dialog" aria-label="Branches" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern">
                            <div>
                                <h2>Branches</h2>
                                <p className="lp-branches-sub">{branchPage.title}</p>
                            </div>
                            <button className="modal-close" onClick={() => setBranchPage(null)} aria-label="Close"><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <h4 className="lp-branches-heading">Live on</h4>
                            {(branchPage.liveOn || []).length === 0 ? (
                                <p className="lp-branches-empty">No branch uses this page yet — guests there see their classic portal page.</p>
                            ) : (
                                <ul className="lp-branches-list">
                                    {branchPage.liveOn.map((b) => (
                                        <li key={b._id}>
                                            <span><i className="fas fa-broadcast-tower"></i> {b.name}</span>
                                            <button type="button" className="btn-secondary-modern" disabled={branchBusy} onClick={() => handleStopOnBranch(b)}>
                                                Stop using
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}

                            <h4 className="lp-branches-heading">Use on a branch</h4>
                            {branchPage.status !== 'published' ? (
                                <p className="lp-branches-empty">Publish the page first — guests only ever see a published page.</p>
                            ) : branchesFor(branchPage).length === 0 ? (
                                <p className="lp-branches-empty">
                                    {branchPage.branchId
                                        ? `This page was made for ${branchPage.branchId.name || 'its branch'} and is live there already.`
                                        : 'Every branch of this company already uses this page.'}
                                </p>
                            ) : (
                                <div className="lp-branches-use">
                                    <select
                                        className="form-control"
                                        aria-label="Branch"
                                        value={branchChoice}
                                        onChange={(e) => setBranchChoice(e.target.value)}
                                    >
                                        <option value="">Select a branch...</option>
                                        {branchesFor(branchPage).map((b) => <option key={b._id} value={b._id}>{b.name}</option>)}
                                    </select>
                                    <button type="button" className="btn-primary-modern" disabled={!branchChoice || branchBusy} onClick={handleUseOnBranch}>
                                        Use on this branch
                                    </button>
                                </div>
                            )}
                            <small className="form-hint lp-branches-hint">
                                A branch shows one page at a time, so using this page on a branch replaces the page it showed before.
                                A page made for one branch stays on that branch; create a page with no branch (tenant-wide) to share one design across branches.
                                Each branch shows its own logo from Branches → Branding, else the company logo.
                            </small>
                        </div>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay-modern" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-modern modal-small" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-modern danger">
                            <div><h2>Delete Landing Page</h2></div>
                            <button className="modal-close" onClick={() => setShowDeleteModal(false)}><i className="fas fa-times"></i></button>
                        </div>
                        <div className="modal-body-modern">
                            <p>Are you sure you want to delete "{pageToDelete?.title}"? This cannot be undone.</p>
                        </div>
                        <div className="modal-footer-modern">
                            <button className="btn-secondary-modern" onClick={() => setShowDeleteModal(false)}>Cancel</button>
                            <button className="btn-danger-modern" onClick={confirmDelete}>
                                <i className="fas fa-trash"></i> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
